import { cn } from "@/lib/utils";

/**
 * Icon tile — the tinted square that marks a section header, a card, a
 * dialog title or an empty state (docs/design-guidelines.md §4 "Section
 * header with coral icon tile", §2 "Coral accent", §13 "Iconography").
 * One component so a tile is always one of four sizes and one of these
 * tones instead of eight sizes, two tints and four radii.
 *
 * `shape="round"` is the same tile as a circle — the tinted disc that marks a
 * callout, a confirmation dialog or a success/empty state. Not for avatars,
 * initials (InitialsTile in components/home/HomePrimitives), numbered step
 * circles or spinners: those carry text or a person, not an icon.
 */
export const ICON_TILE_SIZES = {
  // Inline beside a row title or a form label.
  sm: { tile: "h-8 w-8 rounded-lg", icon: "h-4 w-4" },
  // Card header, dialog title, list row.
  md: { tile: "h-10 w-10 rounded-lg", icon: "h-5 w-5" },
  // §4 section header.
  lg: { tile: "h-11 w-11 rounded-xl", icon: "h-5 w-5" },
  // Hero — an empty state or a page-level illustration.
  xl: { tile: "h-14 w-14 rounded-2xl", icon: "h-7 w-7" },
};

// A tile sitting on a panel of its own hue (an action-50 banner, an emerald-50
// success card) would vanish at -50, so `onTint` steps the fill to -100.
export const ICON_TILE_TONES_ON_TINT = {
  brand: "bg-brand-100 text-brand-700",
  action: "bg-action-100 text-action-700",
  neutral: "bg-gray-200 text-gray-600",
  success: "bg-emerald-100 text-emerald-700",
  warning: "bg-warning-100 text-warning-700",
  danger: "bg-rose-100 text-rose-700",
  premium: "bg-premium-100 text-premium-700",
  solid: "bg-action-600 text-white",
};

// The square sizes' radii, swapped for a circle by `shape="round"`.
const SQUARE_RADIUS = /\brounded-(?:lg|xl|2xl)\b/;

export const ICON_TILE_TONES = {
  // Coral: section headers only (§2).
  brand: "bg-brand-50 text-brand-600",
  action: "bg-action-50 text-action-600",
  neutral: "bg-gray-100 text-gray-600",
  success: "bg-emerald-50 text-emerald-600",
  warning: "bg-warning-50 text-warning-700",
  danger: "bg-rose-50 text-rose-600",
  premium: "bg-premium-50 text-premium-700",
  // Brand-mark tile in a dark or hero header (§2).
  solid: "bg-action-600 text-white",
};

/**
 * @param {object} props
 * @param {React.ElementType} [props.icon]        Heroicon or lucide component.
 * @param {keyof ICON_TILE_SIZES} [props.size]     Default "md".
 * @param {keyof ICON_TILE_TONES} [props.tone]     Default "neutral".
 * @param {"square"|"round"} [props.shape]         Default "square".
 * @param {boolean} [props.onTint]                 On a panel of the same hue:
 *                                                 one shade darker so it shows.
 * @param {number} [props.strokeWidth]             Default 1.75 (§13).
 * @param {string} [props.className]               Layout only (margins, mx-auto).
 * @param {React.ElementType} [props.as]          "span" inside a heading, label
 *                                                 or link, where a div may not sit.
 */
export default function IconTile({
  as: Tag = "div",
  icon: Icon,
  size = "md",
  tone = "neutral",
  shape = "square",
  onTint = false,
  strokeWidth = 1.75,
  className,
  children,
  ...props
}) {
  const s = ICON_TILE_SIZES[size] || ICON_TILE_SIZES.md;
  return (
    <Tag
      className={cn(
        "flex shrink-0 items-center justify-center",
        shape === "round" ? s.tile.replace(SQUARE_RADIUS, "rounded-full") : s.tile,
        (onTint ? ICON_TILE_TONES_ON_TINT : ICON_TILE_TONES)[tone] ||
          ICON_TILE_TONES.neutral,
        className,
      )}
      aria-hidden={Icon ? true : undefined}
      {...props}
    >
      {Icon ? <Icon className={s.icon} strokeWidth={strokeWidth} /> : children}
    </Tag>
  );
}
